/**
 * Generic confetti helper used by every game's win/celebration path.
 *
 * Backed by the @hiseb/confetti CDN script loaded in index.html, which exposes
 * a global `confetti({ position, count, size, velocity, fade })` function.
 *
 * Tuning lives in `data/shared.config.js → confetti`. Each game just calls
 * `celebrate()` on win; pass an `opts` object only when you want to override
 * the defaults for a one-off effect.
 *
 * Public API:
 *   celebrate()                    → fires the configured burst sequence
 *   celebrate({ bursts, delayMs }) → override any field for this call
 *   burst({ x, y })                → single burst at one absolute position
 *
 * Gracefully no-ops if the global `confetti` isn't loaded (offline / blocked).
 */

import { SHARED_CONFIG } from '../../data/shared.config.js';

// Pop sound fired once per confetti burst (every `fire()` below).
const CONFETTI_SFX_URL = 'assets/confetti/confetti-sound.mp3';
// Keep the pop modest — a single celebration fires many bursts
// (celebrate's bursts + rain's columns) and they overlap.
const CONFETTI_SFX_VOLUME = 0.45;

function getCfg() {
    return SHARED_CONFIG.confetti || {};
}

/**
 * Play one short confetti-pop sound. A fresh element per call so
 * rapid back-to-back bursts overlap instead of cutting each other
 * off. Best-effort: any failure (missing file / autoplay block /
 * non-browser env) is swallowed so audio never breaks the VFX.
 */
function playPop() {
    if (typeof Audio === 'undefined') return;
    try {
        const el = new Audio(CONFETTI_SFX_URL);
        el.volume = CONFETTI_SFX_VOLUME;
        const p = el.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {
        // Audio unavailable — ignore.
    }
}

/**
 * Play one confetti-pop sound on demand — the SAME pop every win-screen
 * burst fires. Exposed so in-engine particle bursts (e.g. a localised
 * goal-cell firework rendered in the game's own canvas) can borrow the
 * celebration pop without going through the screen-space `confetti()`
 * library. Best-effort + non-fatal, same as every other audio path.
 */
export function pop() {
    playPop();
}

function fire(position, opts) {
    const g = typeof window !== 'undefined' ? window.confetti : null;
    if (typeof g !== 'function') return;
    // One pop per burst — this is the single chokepoint every game's
    // win celebration funnels through (celebrate / rain / partyMode).
    playPop();
    try {
        g({
            position,
            count: opts.count,
            size: opts.size,
            velocity: opts.velocity,
            fade: opts.fade,
        });
    } catch {
        // Library failed (e.g. blocked) — silently ignore.
    }
}

/**
 * Fire the configured burst sequence. Reads defaults from
 * SHARED_CONFIG.confetti; pass `opts` to override for one call.
 *
 * @param {{ bursts?: number, delayMs?: number, count?: number, size?: number,
 *           velocity?: number, fade?: boolean,
 *           positions?: Array<{x: number, y: number}> }} [opts]
 */
export function celebrate(opts = {}) {
    const cfg = { ...getCfg(), ...opts };
    const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const h = typeof window !== 'undefined' ? window.innerHeight : 768;

    const rel = (opts.positions && opts.positions.length)
        ? opts.positions
        : (cfg.positions || []);

    const positions = rel
        .slice(0, Math.max(0, cfg.bursts ?? rel.length))
        .map((p) => ({ x: w * p.x, y: h * p.y }));

    positions.forEach((pos, i) => {
        setTimeout(() => fire(pos, cfg), i * (cfg.delayMs ?? 500));
    });
}

/**
 * One-shot burst at a specific ABSOLUTE position (e.g. on a click).
 * @param {{ x: number, y: number, count?: number, size?: number,
 *           velocity?: number, fade?: boolean }} pos
 */
export function burst(pos) {
    const cfg = { ...getCfg(), ...pos };
    fire({ x: pos.x, y: pos.y }, cfg);
}

/**
 * Fireworks — a rapid "bom-bom-bom-bom" sequence of N punchy bursts at
 * scattered positions across the upper-central band of the viewport,
 * each with its own pop sound. Built for per-EVENT celebrations
 * (e.g. a single train reaching its destination) where we want the
 * energy + pops of the win screen, fired repeatedly, without the full
 * win-then-rain finale.
 *
 * Each shot is a full-strength burst; they overlap slightly so the
 * screen reads as a real firework volley rather than one big blob.
 *
 * @param {{ shots?: number, gapMs?: number, count?: number, size?: number,
 *           velocity?: number, fade?: boolean,
 *           focus?: { x: number, y: number } }} [opts]
 *   focus — optional relative (0..1) point to cluster the volley around
 *           (e.g. a destination on screen); omit to scatter wide.
 */
export function fireworks(opts = {}) {
    const cfg = { ...getCfg(), ...opts };
    const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const h = typeof window !== 'undefined' ? window.innerHeight : 768;
    const shots = Math.max(1, opts.shots ?? 5);
    const gap = opts.gapMs ?? 150;
    // Cluster tightly around a focus point if given, else scatter across
    // the central-upper band so the volley spreads over the whole stage.
    const fx = opts.focus ? opts.focus.x : 0.5;
    const fy = opts.focus ? opts.focus.y : 0.32;
    const spreadX = opts.focus ? 0.16 : 0.32;
    const spreadY = opts.focus ? 0.12 : 0.18;

    for (let i = 0; i < shots; i++) {
        const x = w * Math.min(0.95, Math.max(0.05, fx + (Math.random() - 0.5) * 2 * spreadX));
        const y = h * Math.min(0.85, Math.max(0.08, fy + (Math.random() - 0.5) * 2 * spreadY));
        setTimeout(() => fire({ x, y }, cfg), i * gap);
    }
}

/**
 * Rain shower — fires N bursts evenly spaced across the top edge of the
 * viewport, with high velocity so particles fall down across the whole
 * screen. Defaults pulled from SHARED_CONFIG.confetti.rain.
 *
 * @param {{ columns?: number, columnDelayMs?: number, count?: number,
 *           size?: number, velocity?: number, fade?: boolean }} [opts]
 */
export function rain(opts = {}) {
    const base = getCfg();
    const cfg = { ...(base.rain || {}), ...opts };
    const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const cols = Math.max(1, cfg.columns ?? 7);
    const gap = cfg.columnDelayMs ?? 80;

    for (let i = 0; i < cols; i++) {
        const x = w * ((i + 0.5) / cols);
        setTimeout(() => fire({ x, y: 0 }, cfg), i * gap);
    }
}

/**
 * Full win celebration: burst sequence followed by a rain shower.
 * Equivalent to calling `celebrate()` then `rain()` after the bursts finish.
 */
export function partyMode(opts = {}) {
    celebrate(opts);
    const cfg = { ...getCfg(), ...opts };
    const bursts = cfg.bursts ?? 3;
    const delay = cfg.delayMs ?? 500;
    setTimeout(() => rain(opts.rain), bursts * delay);
}

export default celebrate;
